'use client';

import attendantFrontend from '@bells/api/attendants/frontend';
import { generateRestClient } from '@bells/core/rest/react';
import { baseLocale, type Locale } from '@bells/i18n/runtime';
import { tags } from '@bells/iso/data/countries';
import {
  AttendantFilteredKeysSchema,
  AttendantSearchSortBy,
} from '@bells/schema/models/attendant';
import { ClientInferResponses } from '@ts-rest/core';
import { defaults, isEqual, isUndefined, omit, omitBy } from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';
import {
  AttendantLimit,
  checkHasFilters,
  maximalAge,
  maximalHeight,
  minimalAge,
  minimalHeight,
  SearchParamsSchema,
  type AttendantSearchParams,
} from './utils';
const attendantRestClient = generateRestClient(attendantFrontend);

export interface FilterContextProps {
  filters: AttendantSearchParams;
  totalFilters: number;
  reset: () => void;
  data?: ClientInferResponses<typeof attendantFrontend.search, 200>;
  isLoading: boolean;
  setFilters: Dispatch<SetStateAction<AttendantSearchParams>>;
  isPlaceholderData: boolean;
  locale: Locale;
  showFilter: boolean;
  setShowFilter: (val: boolean) => void;
  showFilterDesktop: boolean;
  setShowFilterDesktop: (val: boolean) => void;
  filterConfiguration?: z.infer<typeof AttendantFilteredKeysSchema>;
}

export const FilterContext = createContext<FilterContextProps>({
  totalFilters: 0,
  filters: {},
  reset: () => {},
  data: undefined,
  isLoading: false,
  setFilters: () => {},
  isPlaceholderData: true,
  showFilter: false,
  setShowFilter: () => {},
  showFilterDesktop: true,
  setShowFilterDesktop: () => {},
  locale: baseLocale,
});

export const useFilter = (): FilterContextProps => useContext(FilterContext);

export const FilterProvider = ({
  children,
  initFilter,
  initSearchParams,
  location,
  locale = baseLocale,
  initialData,
  filterConfiguration,
  initQuery,
}: PropsWithChildren<{
  initFilter?: { [key: string]: string };
  updatePath?: boolean;
  initSearchParams?: { [key: string]: string };
  locale?: Locale;
  location?: { lng: number; lat: number; country?: (typeof tags)[number] };
  initialData?: ClientInferResponses<
    typeof attendantFrontend.search,
    200
  >['body'];
  initQuery?: any;
  filterConfiguration?: z.infer<typeof AttendantFilteredKeysSchema>;
}>) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [showFilterDesktop, setShowFilterDesktop] = useState<boolean>(true);
  const allDefaultSettings: AttendantSearchParams = {
    page: 1,
    limit: AttendantLimit,
    lng: location?.lng,
    lat: location?.lat,
    country: location?.country,
    language: locale,
    sortBy: location
      ? AttendantSearchSortBy.DISTANCE_ASC
      : AttendantSearchSortBy.LIKES_DESC,
  };

  const newParamData = useMemo(() => {
    const paramsObject: { [key: string]: string } = {};

    searchParams.forEach((value, key) => {
      paramsObject[key] = value;
    });

    const result = SearchParamsSchema.safeParse(paramsObject);
    if (result.success) {
      return result.data as AttendantSearchParams;
    } else {
      return {};
    }
  }, [searchParams]);

  const [filters, setFilters] = useState<AttendantSearchParams>(
    defaults(
      newParamData,
      SearchParamsSchema.parse(initSearchParams) as AttendantSearchParams,
      SearchParamsSchema.parse(initFilter) as AttendantSearchParams,
      allDefaultSettings
    )
  );

  const reset = () => {
    setFilters((prev) => ({ ...allDefaultSettings, sortBy: prev.sortBy }));
  };

  const [cachedFilter] = useDebounce<string>(
    JSON.stringify(omit(filters, ['name'])),
    250
  );
  const isDirty = useMemo<boolean>(() => {
    const newQuery = omitBy({ ...omit(filters, ['name']) }, isUndefined);
    const oldQuery = omitBy({ ...omit(initQuery, ['name']) }, isUndefined);

    return !isEqual(oldQuery, newQuery);
  }, [filters, initQuery]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value == undefined) {
        continue;
      }

      if (
        (key === 'ageFrom' && value === minimalAge) ||
        (key === 'ageTo' && value === maximalAge) ||
        (key === 'heightFrom' && value === minimalHeight) ||
        (key === 'heightTo' && value === maximalHeight)
      ) {
        continue;
      }

      if (key == 'language') {
        continue;
      }

      if (key == 'lng' && value == allDefaultSettings.lng) {
        continue;
      }

      if (key == 'sortBy' && value == allDefaultSettings.sortBy) {
        continue;
      }

      if (key == 'lat' && value == allDefaultSettings.lat) {
        continue;
      }
      if (key == 'limit' && value == allDefaultSettings.limit) {
        continue;
      }

      if (key == 'page' && value == allDefaultSettings.page) {
        continue;
      }

      if (key == 'country' && value == allDefaultSettings.country) {
        continue;
      }

      if (key == 'name' && value == allDefaultSettings.name) {
        continue;
      }

      if (typeof value === 'string') {
        newSearchParams.set(key, value);
      } else if (typeof value === 'number') {
        newSearchParams.set(key, value.toString());
      } else if (typeof value === 'boolean') {
        newSearchParams.set(key, value.toString());
      } else if (value.length > 0) {
        newSearchParams.set(key, value.map((df) => df.toString()).join(','));
      }
    }

    const newPushState = `${pathname}${newSearchParams.size > 0 ? `?${newSearchParams.toString()}` : ''}`;
    const oldPushState = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    if (newPushState != oldPushState) {
      window.history.replaceState({}, '', newPushState);
    }
  }, [filters]);

  const { data, isLoading, isPlaceholderData } =
    attendantRestClient.search.useQuery({
      queryKey: [`attendants`, cachedFilter],
      staleTime: 60000,
      initialData:
        !isDirty && initialData
          ? { status: 200, body: initialData, headers: new Headers() }
          : undefined,
      queryData: {
        query: { ...omit(filters, ['name']) },
      },
      placeholderData: (previousData) => previousData,
    });

  const totalFilters = useMemo(() => {
    return checkHasFilters(filters);
  }, [filters]);

  const config: FilterContextProps = useMemo(() => {
    return {
      filters,
      filterConfiguration,
      setFilters: setFilters,
      reset,
      locale: locale,
      totalFilters: totalFilters,
      data,
      isLoading,
      isPlaceholderData,
      setShowFilter,
      showFilter,
      showFilterDesktop,
      setShowFilterDesktop,
    };
  }, [
    filters,
    data,
    isPlaceholderData,
    showFilterDesktop,
    isLoading,
    showFilter,
    locale,
    filterConfiguration,
  ]);

  return (
    <FilterContext.Provider value={config}>{children}</FilterContext.Provider>
  );
};
